import { Box, Radio, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '@lingui/macro';
import lodash from 'lodash';
import searchPlaces from '../api/geocoding';
import appRoutes from '../utils/routes';
import StyledButton from '../components/StyledButton';
import StyledPaper from '../components/StyledPaper';
import { useAppDispatch, useAppSelector } from '../hooks';
import currentOrderSlice from '../store/orderSlice';
import { TGeocodingResponse, TLocationCoordinate } from '../types';
import EditablePaper from '../components/EditablePaper';
import SecondaryHeader from '../components/SecondaryHeader';


const AddressPicker: React.FC<{ place: TGeocodingResponse, isSelected: boolean, onAddressClick: () => void }> = (props) => {
    const { place, isSelected, onAddressClick } = props;

    return <StyledPaper sx={{ display: 'flex', alignItems: 'center' }} onClick={onAddressClick}>
        <Box flexGrow={1}>
            <Typography variant='body1'>{place.place}</Typography>
            <Typography variant='subtitle1'>{place.fullAddress}</Typography>
        </Box>
        <Box>
            <Radio color="secondary" checked={isSelected} />
        </Box>
    </StyledPaper>
}

const AddressAutocompleteSelect: React.FC<{ places: TGeocodingResponse[], onSelect: (place: TGeocodingResponse) => void }> = ({ places, onSelect }) => {
    const [selectedPlace, setSelectedPlace] = useState<TGeocodingResponse | undefined>()
    return <Box display={'flex'} flexDirection={'column'} flexGrow={1}>
        <Box mb={'12px'} />
        <Box flexGrow={1}>
            {places.map(place => <>
                <AddressPicker
                    place={place}
                    isSelected={place.fullAddress === selectedPlace?.fullAddress}
                    onAddressClick={() => setSelectedPlace(place)}
                />
                <Box mb={'12px'} />
            </>)}
        </Box>
        {selectedPlace && <StyledButton
            disabled={!Boolean(selectedPlace)}
            fullWidth
            variant='contained'
            onClick={() => selectedPlace && onSelect(selectedPlace)}
        >
            {t`Continue`}
        </StyledButton>
        }
    </Box>
}

const AddressSelectionScreen: React.FC = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const initDeliveryLocation = useAppSelector(state => state.order.deliveryLocation)
    const restaurantCoordinate = useAppSelector(state => state.restaurant.info?.restaurantLocation)

    const [query, setQuery] = useState('');
    const [searchResults, setSearchResults] = useState<TGeocodingResponse[]>([])
    const [deliveryComment, setDeliveryComment] = useState<string | undefined>(initDeliveryLocation?.comment)
    const [selectedAddress, setSelectedAddress] = useState<string | undefined>()
    const [selectedCoordinates, setSelectedCoordinates] = useState<TLocationCoordinate | undefined>()

    const isAddressSelected = selectedAddress && selectedCoordinates;

    const resetLocationSelection = () => {
        setSelectedAddress(undefined)
        setSelectedCoordinates(undefined)
        setDeliveryComment(undefined)
    }

    const setDeliveryLocation = () => {
        if (isAddressSelected && deliveryComment) {
            dispatch(currentOrderSlice.actions.changeDeliveryLocation({
                address: selectedAddress,
                coordinate: selectedCoordinates,
                comment: deliveryComment
            }))
            navigate(appRoutes.checkout)
        }
    }

    const runSearch = lodash.throttle((query: string) => {
        restaurantCoordinate && query
            && searchPlaces(query, restaurantCoordinate).then(response => setSearchResults(response))
    }, 1000)

    return <Box sx={{ height: '100vh' }} display='flex' flexDirection='column'>
        <SecondaryHeader title={t`Choose your address`} />
        <Box pb={'12px'} />
        <Box flexGrow={1}>
            {!isAddressSelected && <Box>
                <TextField
                    fullWidth
                    value={query}
                    placeholder={t`Search`}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const newQuery = (event.target as HTMLInputElement).value;
                        setQuery(newQuery)
                        newQuery.length > 2 && runSearch(newQuery)
                    }}
                />
                <AddressAutocompleteSelect
                    places={searchResults}
                    onSelect={(place) => {
                        setSelectedAddress(place.fullAddress!)
                        setSelectedCoordinates(place.coordinate)
                    }}
                />
            </Box>}
            {isAddressSelected && <EditablePaper
                title={t`Address`}
                onEditClick={resetLocationSelection}
            >
                <Typography variant='body1'>{selectedAddress}</Typography>
                <Typography variant='subtitle1'></Typography>
            </EditablePaper>}
            {isAddressSelected && <Box>
                <Box pb={'12px'} />
                <TextField
                    fullWidth
                    placeholder={t`Floor, door etc.`}
                    value={deliveryComment}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setDeliveryComment((event.target as HTMLInputElement).value as string);
                    }}
                />
            </Box>}
        </Box>
        {isAddressSelected && <Box mb={'12px'}>
            <StyledButton
                fullWidth
                variant='contained'
                onClick={setDeliveryLocation}>
                <Typography>{t`Confirm address`}</Typography>
            </StyledButton>
        </Box>}
    </Box>
}

export default AddressSelectionScreen;